import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-4/3 sm:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
import { ref, watch } from 'vue';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import { kcalToG } from '@ts/common/nutrition';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import {} from '@ts/common/datasets';
import {} from 'chart.js/auto';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartSubstrate',
    props: {
        data: { default: undefined },
        help: { type: Boolean, default: false },
        header: { type: Boolean, default: true },
        tooltip: { type: Boolean, default: true }
    },
    setup(__props) {
        Chart.register(annotationPlugin);
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        let chartObj = undefined;
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.data !== undefined)
                    drawChart(props.data);
            },
            getChart: () => chartObj,
        });
        watch(() => props.data, (newValue) => {
            if (newValue !== undefined)
                drawChart(newValue);
        }, { deep: true });
        function drawChart(data) {
            if (chart.value === null)
                return;
            const cm = data.chart_metabolism;
            const maxPower = Math.ceil(data.threshold_abs);
            // Slice to 0 .. threshold (inclusive)
            const labels = [];
            const pctFat = [];
            const pctCarb = [];
            const absFat = [];
            const absCarb = [];
            let crossoverLabel = null;
            for (let i = 0; i < cm.power.length; i++) {
                const p = cm.power[i];
                if (p > maxPower)
                    break;
                const f = cm.energy_fat_per_hour[i] ?? 0;
                const c = cm.energy_carbo_per_hour[i] ?? 0;
                const total = f + c;
                // At ~zero power both are ~0 -> metabolism is fat-dominated.
                const pf = total > 0 ? (f / total) * 100 : 100;
                labels.push(p.toFixed(0));
                pctFat.push(pf);
                pctCarb.push(100 - pf);
                absFat.push(f);
                absCarb.push(c);
                if (crossoverLabel === null && i > 0 && f < c) {
                    crossoverLabel = p.toFixed(0);
                }
            }
            // Vertical markers for the key training intensities (names mirror Zones.vue).
            // Stagger the labels onto two rows so markers with near-identical power
            // (notably Fatmax ≈ E1) don't collide. The labels sit at the bottom of the
            // plot (position 'end'), so a NEGATIVE yAdjust lifts them upward: keep E1/E2
            // on the baseline row and raise AR/Fatmax one label-height above it. Integer
            // pixels to avoid sub-pixel blur.
            const markerDefs = [
                { power: data.zone1?.avg?.power, label: ___({ en: 'AR', de: 'KB' }), color: '#9ca3af', yAdjust: -32 },
                { power: data.zone2?.avg?.power, label: ___({ en: 'E1', de: 'G1' }), color: '#9ca3af', yAdjust: -64 },
                { power: data.zone3?.avg?.power, label: ___({ en: 'E2', de: 'G2' }), color: '#9ca3af', yAdjust: -64 },
                { power: data.fatmax_abs, label: 'Fatmax', color: '#9ca3af', yAdjust: -32 },
            ];
            const markerAnnotations = markerDefs
                .filter((m) => typeof m.power === 'number' && isFinite(m.power) && m.power > 0 && m.power <= maxPower)
                .map((m, i) => ({
                id: 'marker' + i,
                type: 'line',
                scaleID: 'x',
                value: Math.round(m.power).toFixed(0),
                borderColor: m.color,
                borderDash: [4, 4],
                //   borderWidth: 1.5,
                borderWidth: 2,
                label: {
                    position: 'end',
                    backgroundColor: m.color,
                    content: m.label,
                    display: true,
                    yAdjust: m.yAdjust,
                    // Paint labels in the final draw phase (lines stay at 'afterDatasetsDraw')
                    // so every dashed marker line sits behind every label. Without this, a
                    // line whose power nearly matches a neighbour's (Fatmax ≈ E1) is drawn
                    // over that neighbour's label box.
                    drawTime: 'afterDraw',
                },
            }));
            const totalEnergy = absFat.map((f, i) => f + absCarb[i]);
            const chartData = {
                labels,
                datasets: [
                    {
                        label: ___({ en: 'Carbohydrates', de: 'Kohlenhydrate' }),
                        data: pctCarb,
                        borderColor: '#cdb663',
                        backgroundColor: 'rgba(205, 182, 99, 0.45)',
                        pointRadius: 0,
                        borderWidth: 2,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: ___({ en: 'Fat', de: 'Fett' }),
                        data: pctFat,
                        borderColor: '#2fb8c2',
                        backgroundColor: 'rgba(47, 184, 194, 0.45)',
                        pointRadius: 0,
                        borderWidth: 2,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: ___({ en: 'Energy demand', de: 'Energiebedarf' }),
                        data: totalEnergy,
                        // borderColor: '#9ca3af',
                        borderColor: '#636362',
                        // borderDash: [6, 4],
                        pointRadius: 0,
                        borderWidth: 2,
                        fill: false,
                        yAxisID: 'y1',
                    },
                ],
            };
            const chartOptions = {
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                events: props.tooltip ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : [],
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: ___({ en: 'Power [W]', de: 'Leistung [W]' }),
                        },
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 12,
                        },
                    },
                    y: {
                        stacked: true,
                        position: 'left',
                        min: 0,
                        max: 100,
                        title: {
                            display: true,
                            text: ___({ en: 'Share of energy supply [%]', de: 'Anteil an der Energiebereitstellung [%]' }),
                        },
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y1: {
                        type: 'linear',
                        position: 'right',
                        min: 0,
                        title: {
                            display: true,
                            text: ___({ en: 'Energy demand [kcal/h]', de: 'Energiebedarf [kcal/h]' }),
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                        ticks: {
                            autoSkip: true,
                        },
                    },
                },
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    // Draw all area fills before any dataset line so the (gray, dashed)
                    // energy-demand line is never tinted by the translucent fat/carb areas.
                    filler: {
                        drawTime: 'beforeDatasetsDraw',
                    },
                    ...(props.tooltip
                        ? {
                            tooltip: {
                                callbacks: {
                                    title: (context) => {
                                        return ___({
                                            en: 'At ' + context[0].label + ' W:',
                                            de: 'Bei ' + context[0].label + ' W:',
                                        });
                                    },
                                    label: (context) => {
                                        const idx = context.dataIndex;
                                        const di = context.datasetIndex;
                                        const v = context.parsed.y;
                                        if (v === null || v === undefined)
                                            return '';
                                        if (di === 2) {
                                            // Energy-demand line (kcal/h)
                                            return context.dataset.label + ': ' + toFixedNumberStr(v, 0) + ' kcal/h';
                                        }
                                        const isCarb = di === 0;
                                        const kcal = isCarb ? absCarb[idx] : absFat[idx];
                                        const g = kcalToG(kcal, isCarb ? 'carb' : 'fat');
                                        return (context.dataset.label +
                                            ': ' +
                                            toFixedNumberStr(v, 0) +
                                            ' % (' +
                                            toFixedNumberStr(g, 1) +
                                            ' g/h ⇔ ' +
                                            toFixedNumberStr(kcal, 0) +
                                            ' kcal/h)');
                                    },
                                    labelColor: (context) => {
                                        return {
                                            borderColor: 'rgba(0, 0, 0, 0)',
                                            backgroundColor: context.dataset.borderColor ?? 'rgba(0, 0, 0, 0)',
                                            borderWidth: 0,
                                        };
                                    },
                                },
                            },
                        }
                        : {
                            tooltip: {
                                enabled: false,
                            },
                        }),
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw',
                        },
                        annotations: [
                            ...(crossoverLabel === null
                                ? []
                                : [
                                // {
                                //   id: 'crossover',
                                //   type: 'line',
                                //   scaleID: 'x',
                                //   value: crossoverLabel,
                                // //   borderColor: '#636362',
                                //   borderColor: '#9ca3af',
                                // //   borderDash: [10, 5],
                                // //   borderDashOffset: 5,
                                // //   borderWidth: 2,
                                //   borderDash: [4, 4],
                                //   borderWidth: 2,
                                //   label: {
                                //     position: 'end',
                                //     // yAdjust: -64,
                                //     yAdjust: 0,
                                //     // backgroundColor: '#636362',
                                //     backgroundColor: '#9ca3af',
                                //     content: ___({en: 'Crossover (50%/50%)', de: 'Crossover (50%/50%)'}),
                                //     display: true,
                                //     drawTime: 'afterDraw', // keep marker lines behind this label too (see markerAnnotations)
                                //   },
                                // },
                                ]),
                            ...markerAnnotations,
                        ],
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("div", _hoisted_1, [
                    (__props.header)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({ en: 'Substrate Split', de: 'Substratverteilung' })), 1 /* TEXT */),
                                (__props.help)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                        type: "button",
                                        class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                        "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                        title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                    }, [
                                        _cache[0] || (_setBlockTracking(-1, true),
                                            (_cache[0] = _createVNode(SvgVue, {
                                                class: "w-4 h-4",
                                                icon: "lucide/circle-question-mark"
                                            })).cacheIndex = 0,
                                            _setBlockTracking(1),
                                            _cache[0])
                                    ], 8 /* PROPS */, _hoisted_5))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                                }, [
                                    _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(___)({
                                        en: 'This graph shows the relative share of fat and carbohydrates in energy supply up to the anaerobic threshold. The crossover point marks the power at which carbohydrates overtake fat as the dominant fuel.',
                                        de: 'Diese Grafik zeigt den relativen Anteil von Fett und Kohlenhydraten an der Energiebereitstellung bis zur anaeroben Schwelle. Der Crossover-Punkt markiert die Leistung, ab der Kohlenhydrate das Fett als dominanten Energieträger ablösen.',
                                    })), 1 /* TEXT */)
                                ], 2 /* CLASS */))
                                : _createCommentVNode("v-if", true)
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", {
                            class: "absolute inset-0",
                            ref_key: "containerRef",
                            ref: containerRef
                        }, [
                            _createElementVNode("canvas", {
                                class: "w-full h-full",
                                ref_key: "chart",
                                ref: chart
                            }, null, 512 /* NEED_PATCH */)
                        ], 512 /* NEED_PATCH */),
                        (showPlaceholder.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createElementVNode("div", null, [
                                    _cache[2] || (_setBlockTracking(-1, true),
                                        (_cache[2] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/chart-line"
                                        })).cacheIndex = 2,
                                        _setBlockTracking(1),
                                        _cache[2]),
                                    _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ])
            ]));
        };
    }
});
